import { useQueries, useQuery } from 'react-query'

import { medicareApiClient, MedicareApiHeaderNames } from 'clients/medicareApiClient'

import { BeneficiaryGetResponse, BeneficiaryModelOptions } from '@cms-max/medicare-api-sdk'
import { BaseUser, UserWithFields } from 'models/User'

export const useUserQuery = () => {
  const fetchUser = async () => {
    const beneficiaryResponse = await medicareApiClient.beneficiaryApi.getBeneficiary()
    const sessionId = beneficiaryResponse.headers[MedicareApiHeaderNames.MaxSession]
    return {
      sessionId,
      user: beneficiaryResponse.data
    }
  }

  return useQuery<{
    user: BaseUser
    sessionId: string
  } | undefined>('user', fetchUser)
}

export const useFullUserQuery = <T extends BeneficiaryModelOptions[]>(fields: T) => {
  const userFieldQueries = useQueries(
    fields.map(field => ({
      queryKey: ['user', field],
      queryFn: async () => {
        const userResponse = await medicareApiClient.asyncGetBeneficiaryFullData([field])

        if (userResponse) {
          return userResponse.user
        } else {
          return {} as BeneficiaryGetResponse
        }
      }
    }))
  )

  const isLoading = userFieldQueries.some((q) => q.isLoading)
  const isError = userFieldQueries.some((q) => q.isError)
  const errors = userFieldQueries.filter((q) => q.isError).map((q) => q.error)

  type UserType = UserWithFields<T[number]>

  const data = (isLoading || isError)
    ? undefined
    : userFieldQueries.reduce<UserType>((a, c) => ({ ...a, ...c.data }), {} as UserType)

  return {
    isLoading,
    isError,
    errors,
    data
  }
}
